import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "~/css/ProgressModal.css";
import { useTranslation } from "react-i18next";

/**
 * プログレスモーダルのプロパティ
 * @property isOpen - モーダルが開いているかどうか
 * @property progress - 進捗度合い(0-100)
 * @property message - 処理中のメッセージ(オプション)
 * @property onClose - モーダルが閉じられたときのコールバック(オプション)
 */
interface ProgressModalProps {
  isOpen: boolean;
  progress: number; // 0-100
  title?: string;
  message?: string;
  errorMessage?: string | null;
  onClose?: () => void;
}

/**
 * プログレスモーダルコンポーネント（React Portal を使用）
 * @param props - プログレスモーダルのプロパティ
 * @returns プログレスモーダルのJSX要素（body 直下に表示）
 */
const ProgressModal: React.FC<ProgressModalProps> = ({
  isOpen,
  progress,
  title,
  message,
  errorMessage,
  onClose,
}) => {
  const { t } = useTranslation();
  // 完了状態を管理
  const [isCompleted, setIsCompleted] = useState(false);
  // エラー発生時の進捗率を固定
  const [frozenProgress, setFrozenProgress] = useState(0);

  useEffect(() => {
    // モーダルが閉じられたら完了状態とフローズン進捗(エラー時に凍結)をリセット
    if (!isOpen) {
      setIsCompleted(false);
      setFrozenProgress(0);
      return undefined;
    }

    return undefined;
  }, [isOpen]);

  // 進捗が100%になったら完了状態に設定
  useEffect(() => {
    if (progress >= 100) {
      setIsCompleted(true);
    } else {
      setIsCompleted(false);
    }
  }, [progress]);

  // エラーメッセージが表示されたときに現在の進捗率で凍結
  useEffect(() => {
    if (errorMessage && frozenProgress === 0) {
      setFrozenProgress(Math.min(progress, 100));
    }
  }, [errorMessage, progress, frozenProgress]);

  // 完了状態から1秒後にモーダルを自動クローズ
  useEffect(() => {
    if (!isCompleted) return undefined;

    const autoCloseTimer = setTimeout(() => {
      if (onClose) {
        onClose();
      }
      setIsCompleted(false);
    }, 1000);

    return () => {
      clearTimeout(autoCloseTimer);
    };
  }, [isCompleted, onClose]);

  // モーダルが開いていない場合は何も表示しない
  if (!isOpen) return null;

  const modalContent = (
    <div className="progress-modal-overlay modal-overlay visible">
      <div className="progress-modal-container">
        <div className="progress-modal-content">
          <h2 className="progress-modal-title">{title}</h2>

          <p className="progress-modal-message">{message}</p>

          {/* エラーメッセージ */}
          {errorMessage && (
            <div className="progress-modal-error">
              <span className="material-symbols-outlined error-icon">error</span>
              <p className="error-message">{errorMessage}</p>
            </div>
          )}

          {/* プログレスバー */}
          <div className="progress-bar-wrapper">
            <div className="progress-bar-background">
              <div
                className={`progress-bar-fill ${errorMessage ? "progress-bar-fill-error" : ""}`}
                style={{
                  width: `${errorMessage ? frozenProgress : Math.min(progress, 100)}%`,
                }}
              />
            </div>
          </div>

          {/* アニメーション中の表示 */}
          <div className="progress-modal-animation">
            {isCompleted ? (
              <span className="material-symbols-outlined check-icon">check_circle</span>
            ) : errorMessage ? (
              <span className="material-symbols-outlined error-animation-icon">error</span>
            ) : (
              <>
                <div className="spinner" />
                <span className="progress-percentage-spinner">
                  {Math.round(Math.min(progress, 100))}%
                </span>
              </>
            )}
          </div>
          <div className="progress-modal-footer">
            <span className="progress-modal-reload-prompt">{t("reloadPrompt")}</span>
            <button
              className="progress-modal-reload-btn button-1"
              onClick={() => {
                window.location.reload();
              }}
            >
              {t("reload")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // React Portal を使用して body 直下に表示
  return createPortal(modalContent, document.body);
};

export default ProgressModal;
